/**
 * Authentication API client
 * Handle login, register, and token management
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface LoginRequest {
  email: string;
  password: string;
  collectivity_id?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: UserResponse;
}

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  collectivity_id: string;
}

export interface RegisterResponse {
  id: string;
  email: string;
  full_name: string;
  company_name: string;
  collectivity_id: string;
}

export interface UserResponse {
  id: string;
  email: string;
  full_name: string;
  company_name: string;
  role: string;
  collectivity_id: string;
  is_active: boolean;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

/**
 * Login with email/password
 */
export async function login(request: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Login failed');
  }

  return response.json();
}

/**
 * Register new member
 */
export async function register(request: RegisterRequest): Promise<RegisterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Registration failed');
  }

  return response.json();
}

/**
 * Get current user profile (requires auth token)
 */
export async function getCurrentUser(token: string): Promise<UserResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user profile');
  }

  return response.json();
}

/**
 * Change user password (requires auth token)
 */
export async function changePassword(
  token: string,
  request: ChangePasswordRequest
): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Change password failed');
  }

  return response.json();
}

/**
 * Wachtwoord vergeten: vraag een reset-mail aan (backoffice-portaal).
 */
export async function wachtwoordVergeten(email: string): Promise<void> {
  await fetch(`${API_BASE_URL}/api/auth/wachtwoord-vergeten`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, portaal: 'backoffice' }),
  });
  // Bewust geen foutmelding op basis van bestaan account (geen enumeratie).
}

/**
 * Controleer of een reset-token geldig is.
 */
export async function checkResetToken(token: string): Promise<boolean> {
  const r = await fetch(`${API_BASE_URL}/api/auth/wachtwoord-reset/${encodeURIComponent(token)}`);
  return r.ok;
}

/**
 * Stel een nieuw wachtwoord in met een reset-token.
 */
export async function wachtwoordReset(token: string, newPassword: string): Promise<void> {
  const r = await fetch(`${API_BASE_URL}/api/auth/wachtwoord-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || 'Wachtwoord opnieuw instellen mislukt');
  }
}

/**
 * Logout (client-side operation in JWT auth)
 */
export async function logout(token: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Logout failed');
  }

  return response.json();
}
